import React, { useEffect } from "react";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";

import { synchronize } from "./synchronize";

type SyncStatus = "idle" | "syncing" | "done" | "error";

const styles = (theme: Theme) =>
  createStyles({
    progressBody: {
      display: "flex",
      alignItems: "center",
      paddingTop: theme.spacing.unit,
      paddingBottom: theme.spacing.unit,
    },
    spinner: {
      marginRight: theme.spacing.unit * 2,
    },
  });

type SyncDialogProps = {
  open: boolean;
  onClose: () => void;
} & WithStyles<typeof styles>;

const SyncDialogUnstyled: React.SFC<SyncDialogProps> = ({ classes, open, onClose }) => {
  const [status, setStatus] = React.useState<SyncStatus>("idle");
  const [errorMessage, setErrorMessage] = React.useState<string>("");

  const isSyncing = status === "syncing";

  const sync = async () => {
    try {
      await synchronize();
      setStatus("done");
    } catch (err) {
      setErrorMessage(err && err.message ? err.message : "Synchronization failed.");
      setStatus("error");
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStatus("syncing");
    setErrorMessage("");
    sync();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleClose = () => {
    if (isSyncing) return;
    setStatus("idle");
    onClose();
  };

  const dialogTitle = isSyncing ? "Synchronizing" : status === "error" ? "Sync Failed" : "Sync Complete";

  const dialogBody = status === "syncing" ? "Synchronizing your data. Please wait..." : status === "error" ? errorMessage : "Synchronization completed successfully.";

  return (
    <Dialog open={open} disableBackdropClick={isSyncing} disableEscapeKeyDown={isSyncing} onClose={handleClose} aria-labelledby="sync-dialog-title">
      <DialogTitle id="sync-dialog-title">{dialogTitle}</DialogTitle>
      <DialogContent>
        <div className={classes.progressBody}>
          {isSyncing && <CircularProgress size={28} className={classes.spinner} style={{ color: "orange" }} />}
          <DialogContentText style={{ margin: 0 }}>{dialogBody}</DialogContentText>
        </div>
      </DialogContent>
      {!isSyncing && (
        <DialogActions>
          <Button onClick={handleClose} color="primary" variant="outlined" autoFocus>
            ok
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default withStyles(styles)(SyncDialogUnstyled);
